import { useEffect, useRef, useState } from 'react';
import MapperLayerShell from './MapperLayerShell';
import type { MapperPluginConfig } from './mapperConfig';
import { clearEmbedSession, sessionFromEmbedToken, setEmbedSession } from './embedContext';

interface EmbedConfigMessage {
  type: 'mapper:config';
  token?: string;
  config?: MapperPluginConfig;
}

/**
 * Hosted embed surface — receives scoped token via origin-validated postMessage only.
 * Never accepts ?token= query params (referrer/history leak). frame-ancestors CSP at nginx.
 */
export default function MapperEmbedPage() {
  const [embedToken, setEmbedToken] = useState<string | null>(null);
  const readyRef = useRef(false);
  const isFramed = window.parent !== window;

  useEffect(() => {
    if (!isFramed) return;

    const onMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;
      const msg = event.data as EmbedConfigMessage;
      if (msg.type !== 'mapper:config' || !msg.token) return;

      const allowlist = Array.isArray(msg.config?.embed?.allowed_origins)
        ? msg.config!.embed!.allowed_origins!.filter(Boolean)
        : [];
      if (allowlist.length === 0 || !allowlist.includes(event.origin)) return;

      const platformEnv =
        (msg.config?.embed as { platform_env?: string } | undefined)?.platform_env === 'prod'
          ? 'prod'
          : 'sandbox';
      const session = sessionFromEmbedToken(msg.token, platformEnv);
      if (session) setEmbedSession(session);
      setEmbedToken(msg.token);
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      clearEmbedSession();
    };
  }, [isFramed]);

  useEffect(() => {
    if (!isFramed || readyRef.current) return;
    readyRef.current = true;
    try {
      const origin = document.referrer ? new URL(document.referrer).origin : window.location.origin;
      window.parent.postMessage({ type: 'mapper:ready' }, origin);
    } catch {
      /* ignore invalid referrer */
    }
  }, [isFramed]);

  if (!isFramed) {
    return <MapperLayerShell fullViewport />;
  }

  if (!embedToken) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 text-sm text-gray-600">
        Waiting for scoped embed token from parent…
      </div>
    );
  }

  return <MapperLayerShell fullViewport />;
}
